import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, FiEdit2, FiSave,
  FiLoader, FiX, FiShield, FiCalendar, FiClock, FiCheck, FiCamera
} from 'react-icons/fi';
import { usersApi, settingsApi } from '../services/api';

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Current user ID (in real app, this would come from auth context)
  const currentUserId = 1;
  
  const [user, setUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const userData = await usersApi.getById(currentUserId);
      setUser(userData);
      setEditForm(userData);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      // Use defaults
      setUser({
        id: 1,
        username: 'admin',
        email: 'admin@trustcenter.io',
        firstName: 'Admin',
        lastName: 'User',
        organization: 'Trust Center',
        department: 'IT Security',
        manager: 'CEO',
        title: 'System Administrator',
        role: 'Admin',
        status: 'Active',
        phone: '+1-555-0100',
        workAddress: '100 Main Street',
        workCity: 'San Francisco',
        workState: 'CA',
        workPostal: '94105',
        workCountry: 'USA',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      setEditForm({
        id: 1,
        username: 'admin',
        email: 'admin@trustcenter.io',
        firstName: 'Admin',
        lastName: 'User',
        organization: 'Trust Center',
        department: 'IT Security',
        manager: 'CEO',
        title: 'System Administrator',
        role: 'Admin',
        status: 'Active',
        phone: '+1-555-0100',
        workAddress: '100 Main Street',
        workCity: 'San Francisco',
        workState: 'CA',
        workPostal: '94105',
        workCountry: 'USA'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await usersApi.update(currentUserId, editForm);
      setUser(editForm);
      setIsEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm(user);
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-[#fff3e6] text-[#ff6600] dark:bg-orange-900/50 dark:text-orange-300';
      case 'Reviewer': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
      case 'User': return 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300';
      case 'Pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300';
      case 'Inactive': return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-blue-500" />
        <span className={`ml-2 text-slate-600`}>Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold text-slate-800`}>My Profile</h1>
          <p className={`mt-1 text-slate-500`}>View and manage your personal information</p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors"
          >
            <FiEdit2 className="w-4 h-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-slate-100 text-slate-700 hover:bg-slate-200`}
            >
              <FiX className="inline mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors disabled:opacity-50"
            >
              {saving ? <FiLoader className="animate-spin" /> : saveSuccess ? <FiCheck /> : <FiSave />}
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className={`lg:col-span-1 rounded-xl border p-6 bg-white border-slate-200`}>
          <div className="text-center">
            {/* Avatar */}
            <div className="relative inline-block mb-4">
              <div className="w-28 h-28 bg-gradient-to-br from-[#0b79b8] to-[#065a8a] rounded-full flex items-center justify-center text-white text-4xl font-bold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 w-9 h-9 bg-[#ff6600] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#e65c00] transition-colors">
                  <FiCamera className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <h2 className={`text-xl font-semibold text-slate-800`}>
              {user?.firstName} {user?.lastName}
            </h2>
            <p className={`text-sm text-slate-500`}>@{user?.username}</p>
            
            <div className="flex justify-center gap-2 mt-3">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${getRoleBadgeColor(user?.role)}`}>
                {user?.role}
              </span>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${getStatusBadgeColor(user?.status)}`}>
                {user?.status}
              </span>
            </div>

            <div className={`mt-6 pt-6 border-t border-slate-200`}>
              <div className="space-y-3 text-left">
                <div className="flex items-center gap-3">
                  <FiMail className={`w-4 h-4 text-slate-500`} />
                  <span className={`text-sm text-slate-600`}>{user?.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <FiPhone className={`w-4 h-4 text-slate-500`} />
                  <span className={`text-sm text-slate-600`}>{user?.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <FiBriefcase className={`w-4 h-4 text-slate-500`} />
                  <span className={`text-sm text-slate-600`}>{user?.title}</span>
                </div>
                <div className="flex items-center gap-3">
                  <FiMapPin className={`w-4 h-4 text-slate-500`} />
                  <span className={`text-sm text-slate-600`}>{user?.workCity}, {user?.workState}</span>
                </div>
              </div>
            </div>

            <div className={`mt-6 pt-6 border-t border-slate-200`}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Member since</span>
                <span className="text-slate-600">
                  {new Date(user?.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className={`lg:col-span-2 rounded-xl border p-6 bg-white border-slate-200`}>
          <h3 className={`text-lg font-semibold mb-6 text-slate-800`}>
            <FiUser className="inline mr-2" />
            Personal Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.firstName || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.firstName}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.lastName || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.lastName}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Username</label>
              <p className={`text-sm text-slate-500`}>{user?.username}</p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.email}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Phone</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.phone}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Title</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.title}</p>
              )}
            </div>
          </div>

          {/* Work Information */}
          <h3 className={`text-lg font-semibold mt-8 mb-6 text-slate-800`}>
            <FiBriefcase className="inline mr-2" />
            Work Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Organization</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.organization || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, organization: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.organization}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Department</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.department || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.department}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Manager</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.manager || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, manager: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.manager}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Role</label>
              <p className={`text-sm text-slate-500`}>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>{user?.role}</span>
                <span className="ml-2 text-xs">(Contact admin to change)</span>
              </p>
            </div>
          </div>

          {/* Address Information */}
          <h3 className={`text-lg font-semibold mt-8 mb-6 text-slate-800`}>
            <FiMapPin className="inline mr-2" />
            Work Address
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Street Address</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.workAddress || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workAddress: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.workAddress}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>City</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.workCity || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workCity: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.workCity}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>State</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.workState || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workState: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.workState}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Postal Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.workPostal || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workPostal: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.workPostal}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 text-slate-700`}>Country</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.workCountry || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, workCountry: e.target.value }))}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                />
              ) : (
                <p className={`text-sm text-slate-600`}>{user?.workCountry}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity & Security Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className={`rounded-xl border p-6 bg-white border-slate-200`}>
          <h3 className={`text-lg font-semibold mb-4 text-slate-800`}>
            <FiClock className="inline mr-2" />
            Recent Activity
          </h3>
          <div className="space-y-4">
            <div className={`flex items-start gap-3 pb-3 border-b border-slate-200`}>
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <FiShield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className={`text-sm font-medium text-slate-800`}>Logged in successfully</p>
                <p className={`text-xs text-slate-500`}>Today at 9:15 AM</p>
              </div>
            </div>
            <div className={`flex items-start gap-3 pb-3 border-b border-slate-200`}>
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <FiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className={`text-sm font-medium text-slate-800`}>Completed assessment review</p>
                <p className={`text-xs text-slate-500`}>Yesterday at 4:30 PM</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#fff3e6] dark:bg-orange-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <FiEdit2 className="w-4 h-4 text-[#ff6600] dark:text-orange-400" />
              </div>
              <div>
                <p className={`text-sm font-medium text-slate-800`}>Updated profile information</p>
                <p className={`text-xs text-slate-500`}>2 days ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Info */}
        <div className={`rounded-xl border p-6 bg-white border-slate-200`}>
          <h3 className={`text-lg font-semibold mb-4 text-slate-800`}>
            <FiShield className="inline mr-2" />
            Security
          </h3>
          <div className="space-y-4">
            <div className={`flex items-center justify-between p-3 rounded-lg bg-slate-50`}>
              <div>
                <p className={`font-medium text-slate-800`}>Two-Factor Auth</p>
                <p className={`text-xs text-slate-500`}>Extra security for your account</p>
              </div>
              <span className="text-xs px-2 py-1 bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400 rounded-full">Not enabled</span>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg bg-slate-50`}>
              <div>
                <p className={`font-medium text-slate-800`}>Last Password Change</p>
                <p className={`text-xs text-slate-500`}>30 days ago</p>
              </div>
              <a href="/settings" className="text-xs text-[#ff6600] hover:underline">Change</a>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg bg-slate-50`}>
              <div>
                <p className={`font-medium text-slate-800`}>Active Sessions</p>
                <p className={`text-xs text-slate-500`}>1 device logged in</p>
              </div>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400 rounded-full">Current</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
